import React, { createContext, useContext, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

interface PathHistoryContextValue {
  getPreviousPath: () => string | null;
  getHistory: () => string[];
}

const PathHistoryContext = createContext<PathHistoryContextValue | undefined>(undefined);

interface PathHistoryProviderProps {
  children: ReactNode;
}

export const PathHistoryProvider: React.FC<PathHistoryProviderProps> = ({ children }) => {
  const location = useLocation();
  const historyRef = useRef<string[]>([]);

  useEffect(() => {
    const currentPath = location.pathname + location.search + location.hash;
    
    // Add current path to history if it's different from the last one
    if (historyRef.current[historyRef.current.length - 1] !== currentPath) {
      historyRef.current.push(currentPath);
      
      // Keep only last 10 paths to avoid memory issues
      if (historyRef.current.length > 10) {
        historyRef.current.shift();
      }
    }
  }, [location]);

  const getPreviousPath = (): string | null => {
    // Get second-to-last path (skip current page which is last in array)
    return historyRef.current.length > 1 
      ? historyRef.current[historyRef.current.length - 1] 
      : null;
  };

  const getHistory = (): string[] => {
    return [...historyRef.current];
  };

  return (
    <PathHistoryContext.Provider value={{ getPreviousPath, getHistory }}>
      {children}
    </PathHistoryContext.Provider>
  );
};

export const usePathHistory = (): PathHistoryContextValue => {
  const context = useContext(PathHistoryContext);
  
  if (!context) {
    throw new Error('usePathHistory must be used within a PathHistoryProvider');
  }
  
  return context;
};

